// WeeklyDigest — Sunday primary AI surface (Phase 27.2).
//
// Replaces the standalone morning nudge + adaptive guidance + weekly
// summary trio with ONE weekly card. The user opens the app on Sunday
// and gets: a short AI summary of last week, the count of pledges
// kept, the upcoming PAWS week, and one reflection prompt.
//
// Mid-week the surface renders as a quieter "Last week's digest"
// recap so Tuesday users still see continuity.

import { ChevronRight } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Skeleton } from '../ui/Skeleton';
import { pawsForWeek, pawsLensForDay, type PAWSLens } from '../../content/pawsCalendar';
import { REFLECTION_PROMPTS_SOFTER, hashDate } from '../../content/digestCopy';

export interface WeeklyDigestData {
  summary: string | null;
  generatedAt: string | null;
  pledgesKept: number;
  pawsWeekIndex: number | null;
  reflection: string;
  /**
   * PR 1C (Gap 5) — Day 30+ phase framing. Server includes this when
   * the user is past Day 30 of their taper. Null otherwise (the Week
   * 1–4 surfaces cover acute withdrawal already).
   */
  dayIndex?: number | null;
  /**
   * PR 2B (Gap 8) — top 3 trigger tags from the week's urge_logs.
   * Rolled up by the weekly cron; empty if the user didn't tag anything.
   */
  topTriggers?: string[];
}

interface WeeklyDigestProps {
  data: WeeklyDigestData | null;
  loading?: boolean;
  onCheckIn?: () => void;
  onOpenInsights?: () => void;
  /**
   * Phase B §P10.6 — honest-day suppression. When true on the day
   * the user reads the digest (today's actual > today's target with
   * margin), swap the "ONE QUESTION" reflection from the API-provided
   * celebratory line to the softer set in digestCopy.ts. The summary
   * paragraph itself is untouched — the AI summary is descriptive,
   * not celebratory.
   */
  honestDay?: boolean;
}

/**
 * Pick the reflection text. When not honestDay, return the
 * digest's own `reflection` (API-provided celebratory line). When
 * honestDay, pick a softer line deterministically from generatedAt
 * — same digest read twice shows the same prompt — falling back to
 * the first softer prompt when generatedAt is null.
 */
function pickReflection(data: WeeklyDigestData, honestDay: boolean): string {
  if (!honestDay) return data.reflection;
  if (data.generatedAt) {
    const idx = hashDate(data.generatedAt) % REFLECTION_PROMPTS_SOFTER.length;
    return REFLECTION_PROMPTS_SOFTER[idx];
  }
  return REFLECTION_PROMPTS_SOFTER[0];
}

export function WeeklyDigest({ data, loading, onCheckIn, onOpenInsights, honestDay = false }: WeeklyDigestProps) {
  if (loading) {
    return (
      <Card accentLeft className="weekly-digest">
        <Skeleton height={14} width="30%" />
        <Skeleton height={20} width="80%" style={{ marginBlockStart: 'var(--space-3)' }} />
        <Skeleton height={14} width="60%" style={{ marginBlockStart: 'var(--space-2)' }} />
      </Card>
    );
  }
  if (!data) return null;

  const paws = data.pawsWeekIndex != null ? pawsForWeek(data.pawsWeekIndex + 1) : null;
  // PR 1C — compute the Day-30+ lens. The component renders this between
  // the summary paragraph and the meta row so users see "what's normal
  // right now" alongside the AI-generated recap.
  const lens: PAWSLens | null =
    data.dayIndex != null && data.dayIndex >= 30 ? pawsLensForDay(data.dayIndex) : null;

  return (
    <Card accentLeft className="weekly-digest">
        <span className="weekly-digest__eyebrow">Sunday digest</span>

        {data.summary ? (
          <p className="weekly-digest__summary">{data.summary}</p>
        ) : (
          <p className="muted text-body weekly-digest__placeholder">
            Your first weekly digest will land after seven days of check-ins.
          </p>
        )}

        {lens ? (
          <div className="weekly-digest__paws-lens">
            <span className="weekly-digest__eyebrow weekly-digest__eyebrow--inner">
              What's normal right now · {lens.rangeLabel}
            </span>
            <p className="weekly-digest__paws-headline">{lens.headline}</p>
            <p className="weekly-digest__paws-reassurance">{lens.reassurance}</p>
          </div>
        ) : null}

        <div className="weekly-digest__meta">
          <span>
            <strong>{data.pledgesKept}</strong>{' '}
            {data.pledgesKept === 1 ? 'pledge' : 'pledges'} this week
          </span>
          {paws ? <span>Next: {paws.title}</span> : null}
        </div>

        {data.topTriggers && data.topTriggers.length > 0 ? (
          <p className="weekly-digest__triggers" aria-label="top triggers this week">
            <span className="weekly-digest__eyebrow weekly-digest__eyebrow--inner">
              Top triggers
            </span>{' '}
            <strong>{data.topTriggers.join(' · ')}</strong>
          </p>
        ) : null}

        <div className="weekly-digest__reflection">
          <span className="weekly-digest__eyebrow weekly-digest__eyebrow--inner">
            One question
          </span>
          <p className="weekly-digest__reflection-text">
            {pickReflection(data, honestDay)}
          </p>
        </div>

        <div className="row" style={{ marginBlockStart: 'var(--space-4)', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {onCheckIn ? (
            <Button variant="tertiary" size="md" onClick={onCheckIn} rightIcon={<Icon icon={ChevronRight} size={14} />}>
              Tonight's check-in
            </Button>
          ) : null}
          {onOpenInsights ? (
            <Button variant="tertiary" size="md" onClick={onOpenInsights}>
              See your patterns →
            </Button>
          ) : null}
        </div>
      </Card>
  );
}
